import * as def from '@/definitions'
import { ColorConstruct } from '@/utilityModels/colorConstruct'
import { Layer } from './layer';
import { Vector2 } from '@/utilityModels/vector2';
import { ArrayHelper } from '@/helper/arrayHelper';
import { PathHelper } from '@/helper/pathHelper';
import { PathLayer } from './pathLayer';
import { SVGPathData } from 'svg-pathdata';

import * as Enumerable from "linq-es5";
import { Rectangle } from '@/utilityModels/rectangle';

// 多角形レイヤー
export class PolygonLayer extends Layer
{
    public get layerType() : string { return "polygon"; }

    // 多角形レイヤーの操作点について
    // 回転を伴うため、
    // 内部データは回転前のデータとして保持する。

    public get x()
    {
        var leftTop = new Vector2(this.originX, this.originY);
        // 回転
        return leftTop.rotateDegrees(this.rotate, this.centerX, this.centerY).x;
    }

    public set x(x)
    {
        // すべての操作点を平行移動する
        var delta = x - this.x;
        var newPoint = new Array();
        for(var point of this._controlPoints)
        {
            newPoint.push(new Vector2(point.x + delta, point.y));
        }
        this._controlPoints = newPoint;
    }

    public get y()
    {
        var leftTop = new Vector2(this.originX, this.originY);
        // 回転
        return leftTop.rotateDegrees(this.rotate, this.centerX, this.centerY).y;
    }

    public set y(y)
    {
        // すべての操作点を平行移動する
        var delta = y - this.y;
        var newPoint = new Array();
        for(var point of this._controlPoints)
        {
            newPoint.push(new Vector2(point.x, point.y + delta));
        }
        this._controlPoints = newPoint;
    }

    public get width()
    {
        return this.getControlPointsBounds().width;
    }

    public set width(width)
    {
        // 幅が小さすぎると、操作点の情報を失うので食い止める
        var fixWidth = Math.max(0.01, width);
        var delta = fixWidth / this.width;
        var newPoints : Array<Vector2> = new Array();
        var left = this.getControlPointsBounds().left;
        for(var point of this._controlPoints)
        {
            var d = point.x - left;
            var nd = d * delta;
            newPoints.push(new Vector2(left + nd, point.y));
        }
        this._controlPoints = newPoints;
    }

    public get height()
    {
        return this.getControlPointsBounds().height;
    }

    public set height(height)
    {
        // 高さが小さすぎると、操作点の情報を失うので食い止める
        var fixHeight = Math.max(0.01, height);
        var delta = fixHeight / this.height;
        var newPoints : Array<Vector2> = new Array();
        var top = this.getControlPointsBounds().top;
        for(var point of this._controlPoints)
        {
            var d = point.y - top;
            var nd = d * delta;
            newPoints.push(new Vector2(point.x, nd));
        }
        this._controlPoints = newPoints;
    }

    private _polygonRotate = 0;
    public get rotate() 
    { 
        return this._polygonRotate; 
    }
    public set rotate(rotate) 
    {
        this._polygonRotate = rotate;
    }

    public get originX()
    {
        return this.getControlPointsBounds().x;
    }

    public get originY()
    {
        return this.getControlPointsBounds().y;
    }

    public get centerX()
    {
        return this.getControlPointsBounds().centerX;
    }

    public get centerY()
    {
        return this.getControlPointsBounds().centerY;
    }

    public isPointContained(x, y)
    {
        var v = new Vector2(x, y);
        var rotated = v.rotateDegrees(-this.rotate, this.centerX, this.centerY);
        var bounds = this.getControlPointsBounds();
        return (bounds.left <= rotated.x && rotated.x <= bounds.right && bounds.top <= rotated.y && rotated.y <= bounds.bottom);
    }

    // 図形の種類 : 角数 * 0: 矢印, 1: 星, 2: なし, 3~: 凸多角形
    private _cornerNumber : number = 0;
    public get cornerNumber() { return this._cornerNumber; }
    public set cornerNumber(cornerNumber) { this._cornerNumber = cornerNumber; }

    // 丸み
    private _radius : number = 0; 
    public get radius() : number
    {
        return this._radius;
    }
    public set radius(radius : number)
    {
        this._radius = radius;
    }

    // 操作点の外接矩形
    private getControlPointsBounds() : Rectangle
    {
        return PathHelper.getBoundsFromPoints(this._controlPoints);
    }

    // 操作点(頂点)
    private _controlPoints : Array<Vector2> = [];
    public get controlPoints() { return this._controlPoints; }
    public set controlPoints(controlPoints) 
    { 
        this._controlPoints = controlPoints;
    }
    public getControlPoint(index : number) { return this._controlPoints[index]; }
    public getRotatedControlPoint(index : number) { return this._controlPoints[index].rotateDegrees(this._polygonRotate, this.centerX, this.centerY); }
    public getControlPointsNumber() { return this._controlPoints.length; }
    public addControlPoint(x, y) 
    { 
        this._controlPoints = ArrayHelper.add(this._controlPoints, new Vector2(x, y)); 
    }
    public insertControlPoint(index, x, y) 
    { 
        this._controlPoints = ArrayHelper.insert(this._controlPoints, new Vector2(x, y), index);
    }
    public removeControlPoint(index) 
    { 
        this._controlPoints = ArrayHelper.removeAt(this._controlPoints, index);
    }
    public moveControlPoint(index, x, y)
    {
        var array = ArrayHelper.clone(this._controlPoints);
        array[index].x = x;
        array[index].y = y;
        this._controlPoints = array;
    }
    // 制御点を表示位置から与える
    public applyViewControlPoints(viewPoints : Array<Vector2>)
    {
        var newPoints = new Array();
        // 新しい制御点における、中心点を求める。
        // まず外接矩形における左上の点、右下の点を求める。(0,0)中心で-rotate回転させ、このときの左上、右下の座標を求める
        var minX = 0;
        var minY = 0;
        var maxX = 0;
        var maxY = 0;
        for(var i = 0; i < viewPoints.length; ++i)
        {
            var p = viewPoints[i];
            var inverse = p.rotateDegrees(-this.rotate, 0, 0);
            minX = (i == 0) ? inverse.x : Math.min(minX, inverse.x);
            minY = (i == 0) ? inverse.y : Math.min(minY, inverse.y);
            maxX = (i == 0) ? inverse.x : Math.max(maxX, inverse.x);
            maxY = (i == 0) ? inverse.y : Math.max(maxY, inverse.y);
        }
        // 点を回転
        var topLeft = (new Vector2(minX, minY)).rotateDegrees(this.rotate, 0, 0);
        var bottomRight = (new Vector2(maxX, maxY)).rotateDegrees(this.rotate, 0, 0);
        // ２点の中心を求める
        var center = new Vector2((topLeft.x + bottomRight.x) / 2, (topLeft.y + bottomRight.y) / 2);
        // この中心点で新しい多角形の制御点を逆回転させて、新しい制御点を求める
        for(var i = 0; i < viewPoints.length; ++i)
        {
            var p = viewPoints[i];
            var inverse = p.rotateDegrees(-this.rotate, center.x, center.y);
            newPoints.push(new Vector2(inverse.x, inverse.y));
        } 
        this._controlPoints = newPoints;
    }

    // 固定タイプ
    // 0: 縦横固定 *WEB/APPではこれを固定状態とする
    // 1; 縦固定 *WEB/APPではこれを非固定とする
    // 2: 横固定 *WEB/APPでは使用しないが、この数値のデータは非固定として扱う
    private _keepRatio : number = 0;
    public get keepRatio() : number
    {
        return this._keepRatio;
    }
    public set keepRatio(keepRatio : number)
    {
        this._keepRatio = keepRatio;
    }

    // 外線色
    private _strokeColor : ColorConstruct = new ColorConstruct();
    public get strokeColor() : ColorConstruct
    {
        return this._strokeColor;
    }
    public set strokeColor(strokeColor : ColorConstruct)
    {
        this._strokeColor = strokeColor;
    }

    // 線幅
    private _strokeWidth : number = 0;
    public get strokeWidth() : number
    {
        return this._strokeWidth;
    }
    public set strokeWidth(strokeWidth : number)
    {
        this._strokeWidth = strokeWidth;
    }

    //! FILE_VERSION == 6 で追加
    // 枠線なし
    private _noStroke : boolean = false;
    public get noStroke() : boolean
    {
        return this._noStroke;
    }
    public set noStroke(noStroke : boolean)
    {
        this._noStroke = noStroke;
    }

    // 塗りつぶしタイプ
    // 0: なし
    // 1: 単色
    // 2: グラデーション
    private _fillType : number = 0;
    public get fillType() : number
    {
        return this._fillType;
    }
    public set fillType(fillType : number)
    {
        this._fillType = fillType;
    }

    // 塗りつぶし色
    private _fillColor : ColorConstruct = new ColorConstruct();
    public get fillColor() : ColorConstruct
    {
        return this._fillColor;
    }
    public set fillColor(fillColor : ColorConstruct)
    {
        this._fillColor = fillColor;
    }

    // 不透明度
    private _opacity : number = 100;
    public get opacity() : number
    {
        return this._opacity;
    }
    public set opacity(opacity : number)
    {
        this._opacity = opacity;
    }

    // グラデーション種別
    // 0: 線形
    // 1: 円形
    private _gradationType : number = 0;
    public get gradationType() { return this._gradationType; }
    public set gradationType(gradationType) { this._gradationType = gradationType; }

    // 線形グラデーション角度(0~360)
    // 0で左 -> 右方向
    private _linearGradationAngle : number = 0;
    public get linearGradationAngle() { return this._linearGradationAngle; }
    public set linearGradationAngle(linearGradationAngle) { this._linearGradationAngle = linearGradationAngle; }

    // 円形グラデーション方向
    // 0: 中央
    // 1: 左上
    // 2: 右上
    // 3: 右下
    // 4: 左下
    private _radialGradationDirection : number = 0;
    public get radialGradationDirection() { return this._radialGradationDirection; }
    public set radialGradationDirection(radialGradationDirection) { this._radialGradationDirection = radialGradationDirection; }

    // グラデーション情報
    // position: 分岐点の位置(0~100)
    // color: 色
    private _gradationInfoArray : Array<{position : number, color : ColorConstruct}> = new Array();
    public get gradationInfoArray() { return this._gradationInfoArray; }
    public set gradationInfoArray(gradationInfoArray) { this._gradationInfoArray = gradationInfoArray; }
    public getGradation(index)
    {
        return this._gradationInfoArray[index];
    }
    public getGradationPointCount()
    {
        return this._gradationInfoArray.length;
    }
    public addGradation(position, color) : number
    {
        this._gradationInfoArray = ArrayHelper.add(this._gradationInfoArray, {position : position, color : color});
        // position順に並び替える
        this._gradationInfoArray = Enumerable.asEnumerable(this._gradationInfoArray).OrderBy(x => x.position).ToArray();
        // 追加したインデックスを返す
        var newIndex = 0;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            if(this._gradationInfoArray[i].position == position && this._gradationInfoArray[i].color.equals(color)) 
            { 
                newIndex = i;
                break; 
            }
        }

        return newIndex;
    }
    public removeGradation(index)
    {
        this._gradationInfoArray = ArrayHelper.removeAt(this._gradationInfoArray, index);
    }
    public changeGradationColor(index, color)
    {
        var clone = ArrayHelper.clone(this._gradationInfoArray);
        clone[index].color = color;
        this._gradationInfoArray = clone;
    }
    public changeGradationPosition(index, position) : number
    {
        var clone = ArrayHelper.clone(this._gradationInfoArray);
        clone[index].position = position;
        var color = clone[index].color;
        this._gradationInfoArray = clone;
        // position順に並び替える
        this._gradationInfoArray = Enumerable.asEnumerable(this._gradationInfoArray).OrderBy(x => x.position).ToArray();
        // 変更後のインデックスを返す
        var newIndex = 0;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            if(this._gradationInfoArray[i].position == position && this._gradationInfoArray[i].color.equals(color)) 
            { 
                newIndex = i;
                break; 
            }
        }

        return newIndex;
    }

    // 現在のグラデーション編集点を一時保持
    public selectedGradationPoint = 0;

    // 辺をタッチしているか?
    public isTouchedOnLine = false;
    // 最後に触った辺の始点インデックス
    public lastTouchedLineStartIndex = 0;

    // 作成
    // cornerNumber : 角数 * 0: 矢印, 1: 星, 2: なし, 3~: 凸多角形
    // autoHeight : 高さを自動設定する
    public static create(cornerNumber : number, autoHeight : boolean, x : number = 0, y : number = 0, width : number = 0, height : number = 0) : PolygonLayer
    {
        var layer = new PolygonLayer();
        layer.keepRatio = 0;
        layer.strokeColor = new ColorConstruct(0, 0, 0);
        layer.strokeWidth = 1.0;
        layer.fillType = 0;
        layer.fillColor = new ColorConstruct(51 ,153, 0);
        layer.rotate = 0;
        layer.controlPoints = PathHelper.createPolygonControlPoints(cornerNumber, x, y, width, height, autoHeight);
        var bounds = PathHelper.getBoundsFromPoints(layer.controlPoints);
        layer.width = width;
        layer.height = bounds.height;
        layer.x = x;
        layer.y = y;
        if(layer.height != height && autoHeight)
        {
            // 高さが変更された場合、y座標をずらす
            layer.y += (height - layer.height) / 2;
        }
        // 初期グラデーション
        layer.addGradation(0, new ColorConstruct(0, 255, 0));
        layer.addGradation(100, new ColorConstruct(255, 255,255));
        layer.linearGradationAngle = 0;

        layer.cornerNumber = cornerNumber;

        return layer;
    }

    // 複製
    public clone()
    {
        var cloned = new PolygonLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : PolygonLayer)
    {
        layer.cornerNumber = this._cornerNumber;
        layer._polygonRotate = this._polygonRotate;
        layer.radius = this._radius;
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            layer._controlPoints.push(new Vector2(this._controlPoints[i].x, this._controlPoints[i].y));
        }
        layer.keepRatio = this._keepRatio;
        layer.strokeColor = this._strokeColor.clone();
        layer.strokeWidth = this._strokeWidth;
        layer.noStroke = this._noStroke;
        layer.fillType = this._fillType;
        layer.fillColor = this._fillColor.clone();
        layer.opacity = this._opacity;
        layer.gradationType = this._gradationType;
        layer.linearGradationAngle = this._linearGradationAngle;
        layer.radialGradationDirection = this._radialGradationDirection;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            layer._gradationInfoArray.push({ 
                position : this._gradationInfoArray[i].position, 
                color : this._gradationInfoArray[i].color.clone()}
            );
        }
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.cornerNumber = this._cornerNumber;
        jsonObject.polygonRotate = this._polygonRotate;
        jsonObject.radius = this._radius;
        jsonObject.controlPoints = this._controlPoints;
        jsonObject.keepRatio = this._keepRatio;
        jsonObject.strokeColor = this._strokeColor;
        jsonObject.strokeWidth = this._strokeWidth;
        jsonObject.noStroke = this._noStroke;
        jsonObject.fillType = this._fillType;
        jsonObject.fillColor = this._fillColor;
        jsonObject.opacity = this._opacity;
        jsonObject.gradationType = this._gradationType;
        jsonObject.linearGradationAngle = this._linearGradationAngle;
        jsonObject.radialGradationDirection = this._radialGradationDirection;
        jsonObject.gradationInfoArray = this._gradationInfoArray;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : PolygonLayer
    {
        var layer = new PolygonLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.cornerNumber != null) { this._cornerNumber = jsonData.cornerNumber; }
        if(jsonData.polygonRotate != null) { this._polygonRotate = jsonData.polygonRotate; }
        if(jsonData.radius != null) { this._radius = jsonData.radius; }
        if(jsonData.controlPoints != null) 
        { 
            for(var i = 0; i < jsonData.controlPoints.length; ++i)
            {   
                this._controlPoints.push(Vector2.createFromJsonData(jsonData.controlPoints[i]));
            }
        }
        if(jsonData.keepRatio != null) { this._keepRatio = jsonData.keepRatio; }
        if(jsonData.strokeColor != null) { this._strokeColor = ColorConstruct.createFromJsonData(jsonData.strokeColor); }
        if(jsonData.strokeWidth != null) { this._strokeWidth = jsonData.strokeWidth; }
        if(jsonData.noStroke != null) { this._noStroke = jsonData.noStroke; }
        if(jsonData.fillType != null) { this._fillType = jsonData.fillType; }
        if(jsonData.fillColor != null) { this._fillColor = ColorConstruct.createFromJsonData(jsonData.fillColor); }
        if(jsonData.opacity != null) { this._opacity = jsonData.opacity; }
        if(jsonData.gradationType != null) { this._gradationType = jsonData.gradationType; }
        if(jsonData.linearGradationAngle != null) { this._linearGradationAngle= jsonData.linearGradationAngle; }
        if(jsonData.radialGradationDirection != null) { this._radialGradationDirection = jsonData.radialGradationDirection; }
        if(jsonData.gradationInfoArray != null) 
        {
            for(var i = 0; i < jsonData.gradationInfoArray.length; ++i)
            {
                this._gradationInfoArray.push({ 
                    position: jsonData.gradationInfoArray[i].position,
                    color : ColorConstruct.createFromJsonData(jsonData.gradationInfoArray[i].color)
                });
            } 
        }

        super.loadParameterFromJson(jsonData);
    }
}